/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  IncomingEventsSortFields,
  IncomingEventsFilterByFields,
  EventsFilterByFields,
  EventsSortFields,
  FilterComparisonOperator,
  SortOrder,
  useGetTimelineEventsQuery,
  useGetPastTimelineEventsQuery,
} from "~/generated/graphql";
import { TIMELINE_PAGE_LIMIT } from "../constants";

type Props = {
  startDate: string;
  endDate: string;
  planIds?: string[];
  actionIds?: string[];
};

export function useGetTimelineEvents({
  startDate,
  endDate,
  planIds,
  actionIds,
}: Props) {
  // Future/incoming events
  const incomingFilterBy = [
    {
      field: IncomingEventsFilterByFields.StartTime,
      operator: FilterComparisonOperator.Ge,
      values: [startDate],
    },
    {
      field: IncomingEventsFilterByFields.StartTime,
      operator: FilterComparisonOperator.Le,
      values: [endDate],
    },
    ...(planIds && planIds.length > 0
      ? [
          {
            field: IncomingEventsFilterByFields.PlanId,
            operator: FilterComparisonOperator.Eq,
            values: planIds,
          },
        ]
      : []),
    ...(actionIds && actionIds.length > 0
      ? [
          {
            field: IncomingEventsFilterByFields.ActionId,
            operator: FilterComparisonOperator.Eq,
            values: actionIds,
          },
        ]
      : []),
  ];

  const {
    data: incomingData,
    loading: incomingLoading,
    error: incomingError,
  } = useGetTimelineEventsQuery({
    variables: {
      page: 1,
      limit: TIMELINE_PAGE_LIMIT,
      sort: {
        field: IncomingEventsSortFields.StartTime,
        order: SortOrder.Asc,
      },
      filter: {
        filterBy: incomingFilterBy,
      },
    },
  });

  // Past events
  const pastFilterBy = [
    {
      field: EventsFilterByFields.StartTime,
      operator: FilterComparisonOperator.Ge,
      values: [startDate],
    },
    {
      field: EventsFilterByFields.StartTime,
      operator: FilterComparisonOperator.Le,
      values: [endDate],
    },
    ...(planIds && planIds.length > 0
      ? [
          {
            field: EventsFilterByFields.PlanId,
            operator: FilterComparisonOperator.Eq,
            values: planIds,
          },
        ]
      : []),
    ...(actionIds && actionIds.length > 0
      ? [
          {
            field: EventsFilterByFields.ActionId,
            operator: FilterComparisonOperator.Eq,
            values: actionIds,
          },
        ]
      : []),
  ];

  const {
    data: pastData,
    loading: pastLoading,
    error: pastError,
  } = useGetPastTimelineEventsQuery({
    variables: {
      page: 1,
      limit: TIMELINE_PAGE_LIMIT,
      sort: {
        field: EventsSortFields.StartTime,
        order: SortOrder.Asc,
      },
      filter: {
        filterBy: pastFilterBy,
      },
    },
  });

  return {
    incomingEvents: incomingData?.incomingEvents?.result ?? [],
    pastEvents: pastData?.eventsView?.result ?? [],
    loading: incomingLoading || pastLoading,
    error: incomingError ?? pastError,
  } as const;
}
